import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PHONE_COUNTRIES, findPhoneCountryByIso } from '../constants/phoneCountries';

const VijuPhoneCountryPicker = ({
  countryIso,
  onCountrySelect,
  phoneValue,
  onPhoneChange,
  phoneInputName,
  phoneInputId,
  error,
  placeholder = 'Teléfono',
  optional = false,
  autoComplete = 'tel-national'
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const selected = findPhoneCountryByIso(countryIso);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PHONE_COUNTRIES;
    return PHONE_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const label = optional ? `${placeholder} (opcional)` : placeholder;

  return (
    <div className={`viju-phone-country-picker${error ? ' has-error' : ''}`} ref={rootRef}>
      <div className="viju-phone-row">
        <button
          type="button"
          className="viju-phone-country-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={`Indicativo ${selected.name}`}
        >
          <span className="viju-phone-flag" aria-hidden="true">
            {selected.flag}
          </span>
          <span className="viju-phone-dial">{selected.dialCode}</span>
          <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
        <input
          type="tel"
          id={phoneInputId}
          name={phoneInputName}
          value={phoneValue}
          onChange={onPhoneChange}
          className={error ? 'error' : ''}
          autoComplete={autoComplete}
          placeholder={label}
          inputMode="tel"
        />
      </div>

      {open && (
        <div className="viju-phone-country-menu" role="listbox" aria-label="País del teléfono">
          <div className="viju-phone-country-search-wrap">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar país o código…"
              autoFocus
              aria-label="Buscar país"
            />
          </div>
          <ul className="viju-phone-country-list">
            {filtered.length === 0 ? (
              <li className="viju-phone-country-empty">Sin resultados</li>
            ) : (
              filtered.map((country) => {
                const isSelected = country.code === selected.code;
                return (
                  <li key={`${country.code}-${country.dialCode}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`viju-phone-country-option${isSelected ? ' is-selected' : ''}`}
                      onClick={() => {
                        onCountrySelect(country);
                        setOpen(false);
                        setSearch('');
                      }}
                    >
                      <span className="viju-phone-flag">{country.flag}</span>
                      <span className="viju-phone-country-name">{country.name}</span>
                      <span className="viju-phone-country-code">{country.dialCode}</span>
                      {isSelected ? <i className="fas fa-check viju-phone-country-check" aria-hidden="true" /> : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}

      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
};

export default VijuPhoneCountryPicker;
